import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { UserPlus, Download, KeyRound, Trash2 } from "lucide-react";
import { eur, exportCSV } from "@/lib/format";
import { Field } from "@/pages/admin/AgentsTab";

export function ManagersTab() {
  const [managers, setManagers] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", commission_pct: "" });
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = useCallback(() => {
    api.get("/admin/managers").then((r) => setManagers(r.data)).catch(() => setManagers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createManager = async () => {
    try {
      await api.post("/admin/managers", { ...form, commission_pct: parseFloat(form.commission_pct) || 0 });
      toast.success("Capo area creato");
      setCreateOpen(false); setForm({ full_name: "", email: "", password: "", commission_pct: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updatePct = async (m, pct) => {
    try { await api.patch(`/admin/managers/${m.id}`, { commission_pct: parseFloat(pct) || 0 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPassword = async () => {
    try { await api.patch(`/admin/managers/${pwdTarget.id}`, { password: newPwd }); toast.success("Password reimpostata"); setPwdTarget(null); setNewPwd(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeManager = async (id) => {
    try { await api.delete(`/admin/managers/${id}`); toast.success("Capo area eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doExport = () => {
    exportCSV("capi_area.csv", managers.map((m) => ({
      Nome: m.full_name, Email: m.email, "Commissione %": m.commission_pct,
      Agenti: m.agent_count, "Premi team": m.team_prizes_won, "Valore premi team €": m.team_prize_value, "Guadagno €": m.earning,
    })));
    toast.success("CSV esportato");
  };

  if (!managers) return <Skeleton className="h-64 bg-[#FFFFFF]" />;

  return (
    <div className="space-y-5" data-testid="managers-tab">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button variant="outline" onClick={doExport} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33]" data-testid="export-managers-csv-btn">
          <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Esporta CSV
        </Button>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="create-manager-btn">
          <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Nuovo capo area
        </Button>
      </div>

      <Card className="bg-[#FFFFFF] border-[#DCE7F5] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                <TableHead className="text-[#5A6B85]">Capo Area</TableHead>
                <TableHead className="text-[#5A6B85] text-center">Commissione %</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Agenti</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Premi team €</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Guadagno €</TableHead>
                <TableHead className="text-[#5A6B85] text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {managers.map((m) => (
                <TableRow key={m.id} className="border-[#DCE7F5]" data-testid={`manager-row-${m.id}`}>
                  <TableCell>
                    <div className="font-medium">{m.full_name}</div>
                    <div className="text-xs text-[#5A6B85]">{m.email}</div>
                  </TableCell>
                  <TableCell className="text-center">
                    <input
                      type="number" defaultValue={m.commission_pct}
                      onBlur={(e) => updatePct(m, e.target.value)}
                      className="w-20 bg-[#F5F9FF] border border-[#DCE7F5] rounded-md px-2 py-1 text-center text-[#0F1E33] focus:outline-none focus:border-[#0B6BCB]"
                      data-testid={`commission-input-${m.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right text-[#5A6B85]">{m.agent_count}</TableCell>
                  <TableCell className="text-right text-[#5A6B85]">{eur(m.team_prize_value)}</TableCell>
                  <TableCell className="text-right font-semibold text-[#0B6BCB]">{eur(m.earning)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-[#0B6BCB]" onClick={() => setPwdTarget(m)} data-testid={`reset-mgr-pwd-${m.id}`}>
                      <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#5A6B85] hover:text-red-600" onClick={() => removeManager(m.id)} data-testid={`delete-manager-${m.id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {managers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#5A6B85] py-8">Nessun capo area</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="create-manager-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuovo capo area</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nome completo" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="manager-name-input" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="manager-email-input" />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="manager-password-input" />
            <Field label="Commissione %" type="number" value={form.commission_pct} onChange={(v) => setForm({ ...form, commission_pct: v })} testid="manager-commission-input" />
          </div>
          <DialogFooter>
            <Button onClick={createManager} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-manager-btn">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#DCE7F5] text-[#0F1E33]" data-testid="reset-mgr-pwd-dialog">
          <DialogHeader><DialogTitle className="font-display">Reset password · {pwdTarget?.full_name}</DialogTitle></DialogHeader>
          <Field label="Nuova password" type="password" value={newPwd} onChange={setNewPwd} testid="new-mgr-password-input" />
          <DialogFooter>
            <Button onClick={resetPassword} className="bg-[#0B6BCB] hover:bg-[#0A5CB0] text-[#FFFFFF] font-semibold" data-testid="save-mgr-password-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
